import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { Asset } from '../../core/types/asset.types';
import { BUILT_IN_COMPONENTS } from '../constants/components';
import { getNextZIndex } from '../utils/z-index-counter';

// ============================================================================
// Types
// ============================================================================

export interface PreviewPanelBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AssetPreviewInstance {
  id: string;
  assetId: string;
  assetType: string;
  assetPath: string;
  assetName: string;
  bounds: PreviewPanelBounds;
  state: 'normal' | 'minimized';
  zIndex: number;
  isDirty: boolean;
  createdAt: number;
}

export interface PrefabGameObject {
  id: string;
  name: string;
  type: string;
  parentId: string | null;
  children: string[];
  transform: {
    position: { x: number; y: number };
    rotation: number;
    scale: { x: number; y: number };
  };
  components: Record<string, unknown>[];
}

export interface PrefabUndoAction {
  type: 'update' | 'add' | 'delete' | 'rename';
  objectId: string;
  previousState: PrefabGameObject | null;
  newState: PrefabGameObject | null;
}

export interface PrefabEditorState {
  rootId: string | null;
  gameObjects: Record<string, PrefabGameObject>;
  selectedIds: string[];
  expandedIds: string[];
  undoStack: PrefabUndoAction[];
  redoStack: PrefabUndoAction[];
  clipboardData: PrefabGameObject | null;
}

export interface AudioPlayerState {
  isPlaying: boolean;
  isPaused: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isLooping: boolean;
  playbackRate: number;
}

interface AssetPreviewStore {
  // State
  previews: Record<string, AssetPreviewInstance>;
  activePreviewId: string | null;
  prefabStates: Record<string, PrefabEditorState>;
  audioStates: Record<string, AudioPlayerState>;

  // Panel actions
  openPreview: (asset: Asset) => string;
  closePreview: (instanceId: string) => void;
  closeAllPreviews: () => void;
  minimizePreview: (instanceId: string) => void;
  restorePreview: (instanceId: string) => void;
  bringToFront: (instanceId: string) => void;
  setPanelBounds: (instanceId: string, bounds: Partial<PreviewPanelBounds>) => void;
  setDirty: (instanceId: string, isDirty: boolean) => void;

  // Prefab actions
  initPrefabState: (instanceId: string, content: string) => void;
  updatePrefabObject: (instanceId: string, objectId: string, updates: Partial<PrefabGameObject>) => void;
  addPrefabChild: (instanceId: string, parentId: string | null, objectType: string) => string;
  deletePrefabObject: (instanceId: string, objectId: string) => void;
  selectPrefabObjects: (instanceId: string, objectIds: string[], multiSelect?: boolean) => void;
  togglePrefabExpanded: (instanceId: string, objectId: string) => void;
  undoPrefab: (instanceId: string) => void;
  redoPrefab: (instanceId: string) => void;
  copyPrefabObject: (instanceId: string) => void;
  pastePrefabObject: (instanceId: string, parentId: string | null) => void;
  updatePrefabComponentData: (instanceId: string, objectId: string, componentType: string, propertyName: string, value: unknown) => void;
  getPrefabJSON: (instanceId: string) => string;

  // Audio actions
  initAudioState: (instanceId: string) => void;
  setAudioPlaying: (instanceId: string, isPlaying: boolean) => void;
  setAudioPaused: (instanceId: string, isPaused: boolean) => void;
  setAudioCurrentTime: (instanceId: string, currentTime: number) => void;
  setAudioDuration: (instanceId: string, duration: number) => void;
  setAudioVolume: (instanceId: string, volume: number) => void;
  setAudioMuted: (instanceId: string, isMuted: boolean) => void;
  setAudioLooping: (instanceId: string, isLooping: boolean) => void;
  setAudioPlaybackRate: (instanceId: string, playbackRate: number) => void;
  stopAudio: (instanceId: string) => void;

  // Getters
  getVisiblePreviews: () => AssetPreviewInstance[];
  getMinimizedPreviews: () => AssetPreviewInstance[];
}

// ============================================================================
// Constants
// ============================================================================

const TOP_OFFSET = 32; // MenuBar height
const HEADER_HEIGHT = 32; // Panel header height (h-8 = 2rem = 32px)
const MIN_VISIBLE_WIDTH = 70; // Minimum visible panel width on screen
const DEFAULT_WIDTH = 480;
const DEFAULT_HEIGHT = 400;
const MIN_WIDTH = 300;
const MIN_HEIGHT = 250;
const CASCADE_OFFSET = 30;

// ============================================================================
// Helpers
// ============================================================================

function generateId(): string {
  return `preview-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function generateObjectId(): string {
  return `obj-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Clamp preview panel bounds to keep panel accessible.
 * - Top: constrained to protect the menubar/toolbar area
 * - Bottom: constrained so title bar remains visible for dragging
 * - Left/Right: constrained so at least MIN_VISIBLE_WIDTH remains on screen
 */
function clampBounds(bounds: PreviewPanelBounds): PreviewPanelBounds {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
  const maxY = viewportHeight - HEADER_HEIGHT;
  const minX = -bounds.width + MIN_VISIBLE_WIDTH;
  const maxX = viewportWidth - MIN_VISIBLE_WIDTH;
  return {
    width: Math.max(MIN_WIDTH, bounds.width),
    height: Math.max(MIN_HEIGHT, bounds.height),
    x: Math.max(minX, Math.min(maxX, bounds.x)), // Left and right constraint
    y: Math.max(TOP_OFFSET, Math.min(maxY, bounds.y)), // Top and bottom constraint
  };
}

function getCascadedPosition(existingCount: number): PreviewPanelBounds {
  const offset = (existingCount % 10) * CASCADE_OFFSET;
  return clampBounds({
    x: 100 + offset,
    y: TOP_OFFSET + 20 + offset,
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
  });
}

/** Infer object type from components (backward compat for prefabs missing `type`) */
function inferTypeFromComponents(components: Record<string, unknown>[]): string {
  if (!components || components.length === 0) return 'empty';
  for (const comp of components) {
    const compType = (comp as { type?: string }).type;
    if (compType === 'sprite-renderer') return 'sprite';
    if (compType === 'text-renderer') return 'text';
    if (compType === 'graphics-renderer') return 'graphics';
  }
  return 'empty';
}

const PREFAB_DEFAULT_COMPONENTS_MAP: Record<string, string[]> = {
  empty: [],
  container: [],
  sprite: ['sprite-renderer'],
  text: ['text-renderer'],
  graphics: ['graphics-renderer'],
};

function createPrefabDefaultComponents(componentTypes: string[]): Record<string, unknown>[] {
  return componentTypes.map((componentType) => {
    const definition = BUILT_IN_COMPONENTS.find((c) => c.type === componentType);
    const data: Record<string, unknown> = {};
    if (definition) {
      for (const prop of definition.properties) {
        if (prop.defaultValue !== undefined) {
          data[prop.name] = prop.defaultValue;
        }
      }
    }
    return { type: componentType, data };
  });
}

function createDefaultPrefabObject(
  id: string,
  name: string,
  parentId: string | null,
  type: string = 'empty'
): PrefabGameObject {
  const componentTypes = PREFAB_DEFAULT_COMPONENTS_MAP[type] || [];
  return {
    id,
    name,
    type,
    parentId,
    children: [],
    transform: {
      position: { x: 0, y: 0 },
      rotation: 0,
      scale: { x: 1, y: 1 },
    },
    components: createPrefabDefaultComponents(componentTypes),
  };
}

// ============================================================================
// Store
// ============================================================================

export const useAssetPreviewStore = create<AssetPreviewStore>()(
  immer((set, get) => ({
    // Initial state
    previews: {},
    activePreviewId: null,
    prefabStates: {},
    audioStates: {},

    // ========================================================================
    // Panel Actions
    // ========================================================================

    openPreview: (asset: Asset) => {
      // Check if preview for this asset already exists
      const existingPreview = Object.values(get().previews).find(
        (p) => p.assetId === asset.id && p.state === 'normal'
      );

      if (existingPreview) {
        // Bring existing preview to front
        get().bringToFront(existingPreview.id);
        return existingPreview.id;
      }

      // Check if there's a minimized preview for this asset
      const minimizedPreview = Object.values(get().previews).find(
        (p) => p.assetId === asset.id && p.state === 'minimized'
      );

      if (minimizedPreview) {
        get().restorePreview(minimizedPreview.id);
        return minimizedPreview.id;
      }

      // Create new preview
      const id = generateId();
      const existingCount = Object.keys(get().previews).length;
      const newZIndex = getNextZIndex();

      set((state) => {
        state.previews[id] = {
          id,
          assetId: asset.id,
          assetType: asset.type,
          assetPath: asset.path,
          assetName: asset.name,
          bounds: getCascadedPosition(existingCount),
          state: 'normal',
          zIndex: newZIndex,
          isDirty: false,
          createdAt: Date.now(),
        };
        state.activePreviewId = id;
      });

      return id;
    },

    closePreview: (instanceId: string) => {
      set((state) => {
        delete state.previews[instanceId];
        delete state.prefabStates[instanceId];
        delete state.audioStates[instanceId];

        if (state.activePreviewId === instanceId) {
          const remaining = Object.values(state.previews).filter((p) => p.state === 'normal');
          state.activePreviewId = remaining.length > 0
            ? remaining.sort((a, b) => b.zIndex - a.zIndex)[0].id
            : null;
        }
      });
    },

    closeAllPreviews: () => {
      set((state) => {
        state.previews = {};
        state.prefabStates = {};
        state.audioStates = {};
        state.activePreviewId = null;
      });
    },

    minimizePreview: (instanceId: string) => {
      set((state) => {
        if (state.previews[instanceId]) {
          state.previews[instanceId].state = 'minimized';

          if (state.activePreviewId === instanceId) {
            const visiblePreviews = Object.values(state.previews).filter(
              (p) => p.state === 'normal'
            );
            state.activePreviewId = visiblePreviews.length > 0
              ? visiblePreviews.sort((a, b) => b.zIndex - a.zIndex)[0].id
              : null;
          }
        }
      });
    },

    restorePreview: (instanceId: string) => {
      const newZIndex = getNextZIndex();
      set((state) => {
        if (state.previews[instanceId]) {
          state.previews[instanceId].state = 'normal';
          state.previews[instanceId].zIndex = newZIndex;
          state.activePreviewId = instanceId;
        }
      });
    },

    bringToFront: (instanceId: string) => {
      const newZIndex = getNextZIndex();
      set((state) => {
        if (state.previews[instanceId]) {
          state.previews[instanceId].zIndex = newZIndex;
          state.activePreviewId = instanceId;
        }
      });
    },

    setPanelBounds: (instanceId: string, bounds: Partial<PreviewPanelBounds>) => {
      set((state) => {
        if (state.previews[instanceId]) {
          const currentBounds = state.previews[instanceId].bounds;
          const newBounds = clampBounds({
            ...currentBounds,
            ...bounds,
          });
          state.previews[instanceId].bounds = newBounds;
        }
      });
    },

    setDirty: (instanceId: string, isDirty: boolean) => {
      set((state) => {
        if (state.previews[instanceId]) {
          state.previews[instanceId].isDirty = isDirty;
        }
      });
    },

    // ========================================================================
    // Prefab Actions
    // ========================================================================

    initPrefabState: (instanceId: string, content: string) => {
      try {
        const data = JSON.parse(content);
        const gameObjects: Record<string, PrefabGameObject> = {};
        let rootId: string | null = null;

        // Parse prefab data
        if (data.gameObjects && typeof data.gameObjects === 'object') {
          for (const [id, obj] of Object.entries(data.gameObjects)) {
            const parsed = obj as PrefabGameObject;
            // Backward compat: infer type from components if missing
            if (!parsed.type) {
              parsed.type = inferTypeFromComponents(parsed.components);
            }
            gameObjects[id] = parsed;
            if (parsed.parentId === null) {
              rootId = id;
            }
          }
        } else if (data.id) {
          // Single root object format
          const parsed = data as PrefabGameObject;
          if (!parsed.type) {
            parsed.type = inferTypeFromComponents(parsed.components);
          }
          gameObjects[data.id] = parsed;
          rootId = data.id;
        } else {
          // Create default root
          rootId = generateObjectId();
          gameObjects[rootId] = createDefaultPrefabObject(rootId, 'Root', null, 'empty');
        }

        set((state) => {
          state.prefabStates[instanceId] = {
            rootId,
            gameObjects,
            selectedIds: rootId ? [rootId] : [],
            expandedIds: rootId ? [rootId] : [],
            undoStack: [],
            redoStack: [],
            clipboardData: null,
          };
        });
      } catch {
        // Invalid JSON - create empty prefab
        const rootId = generateObjectId();
        set((state) => {
          state.prefabStates[instanceId] = {
            rootId,
            gameObjects: {
              [rootId]: createDefaultPrefabObject(rootId, 'Root', null),
            },
            selectedIds: [rootId],
            expandedIds: [rootId],
            undoStack: [],
            redoStack: [],
            clipboardData: null,
          };
        });
      }
    },

    updatePrefabObject: (instanceId: string, objectId: string, updates: Partial<PrefabGameObject>) => {
      set((state) => {
        const prefabState = state.prefabStates[instanceId];
        if (!prefabState || !prefabState.gameObjects[objectId]) return;

        const previousState = { ...prefabState.gameObjects[objectId] };
        const newState = { ...previousState, ...updates };

        prefabState.gameObjects[objectId] = newState;
        prefabState.undoStack.push({
          type: 'update',
          objectId,
          previousState,
          newState,
        });
        prefabState.redoStack = [];

        if (state.previews[instanceId]) {
          state.previews[instanceId].isDirty = true;
        }
      });
    },

    addPrefabChild: (instanceId: string, parentId: string | null, objectType: string) => {
      const newId = generateObjectId();

      set((state) => {
        const prefabState = state.prefabStates[instanceId];
        if (!prefabState) return;

        const actualParentId = parentId || prefabState.rootId;
        if (!actualParentId) return;

        const newObject = createDefaultPrefabObject(
          newId,
          objectType === 'empty' ? 'New Object' : `New ${objectType}`,
          actualParentId,
          objectType
        );

        prefabState.gameObjects[newId] = newObject;

        if (prefabState.gameObjects[actualParentId]) {
          prefabState.gameObjects[actualParentId].children.push(newId);
        }

        prefabState.undoStack.push({
          type: 'add',
          objectId: newId,
          previousState: null,
          newState: newObject,
        });
        prefabState.redoStack = [];

        // Auto-expand parent
        if (!prefabState.expandedIds.includes(actualParentId)) {
          prefabState.expandedIds.push(actualParentId);
        }

        prefabState.selectedIds = [newId];

        if (state.previews[instanceId]) {
          state.previews[instanceId].isDirty = true;
        }
      });

      return newId;
    },

    deletePrefabObject: (instanceId: string, objectId: string) => {
      set((state) => {
        const prefabState = state.prefabStates[instanceId];
        if (!prefabState) return;

        const obj = prefabState.gameObjects[objectId];
        if (!obj || obj.parentId === null) return; // Cannot delete root

        // Remove from parent's children
        const parent = prefabState.gameObjects[obj.parentId];
        if (parent) {
          parent.children = parent.children.filter((id) => id !== objectId);
        }

        // Store for undo
        prefabState.undoStack.push({
          type: 'delete',
          objectId,
          previousState: { ...obj },
          newState: null,
        });
        prefabState.redoStack = [];

        // Remove object and its children recursively
        const deleteRecursive = (id: string) => {
          const toDelete = prefabState.gameObjects[id];
          if (toDelete) {
            toDelete.children.forEach(deleteRecursive);
            delete prefabState.gameObjects[id];
          }
        };
        deleteRecursive(objectId);

        // Update selection
        prefabState.selectedIds = prefabState.selectedIds.filter((id) => id !== objectId);

        if (state.previews[instanceId]) {
          state.previews[instanceId].isDirty = true;
        }
      });
    },

    selectPrefabObjects: (instanceId: string, objectIds: string[], multiSelect = false) => {
      set((state) => {
        const prefabState = state.prefabStates[instanceId];
        if (!prefabState) return;

        if (multiSelect) {
          const newSelection = new Set(prefabState.selectedIds);
          objectIds.forEach((id) => {
            if (newSelection.has(id)) {
              newSelection.delete(id);
            } else {
              newSelection.add(id);
            }
          });
          prefabState.selectedIds = Array.from(newSelection);
        } else {
          prefabState.selectedIds = objectIds;
        }
      });
    },

    togglePrefabExpanded: (instanceId: string, objectId: string) => {
      set((state) => {
        const prefabState = state.prefabStates[instanceId];
        if (!prefabState) return;

        const index = prefabState.expandedIds.indexOf(objectId);
        if (index >= 0) {
          prefabState.expandedIds.splice(index, 1);
        } else {
          prefabState.expandedIds.push(objectId);
        }
      });
    },

    undoPrefab: (instanceId: string) => {
      set((state) => {
        const prefabState = state.prefabStates[instanceId];
        if (!prefabState || prefabState.undoStack.length === 0) return;

        const action = prefabState.undoStack.pop()!;
        prefabState.redoStack.push(action);

        switch (action.type) {
          case 'update':
            if (action.previousState) {
              prefabState.gameObjects[action.objectId] = action.previousState;
            }
            break;
          case 'add':
            if (action.newState && action.newState.parentId) {
              const parent = prefabState.gameObjects[action.newState.parentId];
              if (parent) {
                parent.children = parent.children.filter((id) => id !== action.objectId);
              }
            }
            delete prefabState.gameObjects[action.objectId];
            break;
          case 'delete':
            if (action.previousState) {
              prefabState.gameObjects[action.objectId] = action.previousState;
              if (action.previousState.parentId) {
                const parent = prefabState.gameObjects[action.previousState.parentId];
                if (parent && !parent.children.includes(action.objectId)) {
                  parent.children.push(action.objectId);
                }
              }
            }
            break;
        }

        if (state.previews[instanceId]) {
          state.previews[instanceId].isDirty = prefabState.undoStack.length > 0;
        }
      });
    },

    redoPrefab: (instanceId: string) => {
      set((state) => {
        const prefabState = state.prefabStates[instanceId];
        if (!prefabState || prefabState.redoStack.length === 0) return;

        const action = prefabState.redoStack.pop()!;
        prefabState.undoStack.push(action);

        switch (action.type) {
          case 'update':
            if (action.newState) {
              prefabState.gameObjects[action.objectId] = action.newState;
            }
            break;
          case 'add':
            if (action.newState) {
              prefabState.gameObjects[action.objectId] = action.newState;
              if (action.newState.parentId) {
                const parent = prefabState.gameObjects[action.newState.parentId];
                if (parent && !parent.children.includes(action.objectId)) {
                  parent.children.push(action.objectId);
                }
              }
            }
            break;
          case 'delete':
            if (action.previousState && action.previousState.parentId) {
              const parent = prefabState.gameObjects[action.previousState.parentId];
              if (parent) {
                parent.children = parent.children.filter((id) => id !== action.objectId);
              }
            }
            delete prefabState.gameObjects[action.objectId];
            break;
        }

        if (state.previews[instanceId]) {
          state.previews[instanceId].isDirty = true;
        }
      });
    },

    copyPrefabObject: (instanceId: string) => {
      set((state) => {
        const prefabState = state.prefabStates[instanceId];
        if (!prefabState || prefabState.selectedIds.length === 0) return;

        const objectId = prefabState.selectedIds[0];
        const obj = prefabState.gameObjects[objectId];
        if (obj) {
          prefabState.clipboardData = JSON.parse(JSON.stringify(obj));
        }
      });
    },

    pastePrefabObject: (instanceId: string, parentId: string | null) => {
      set((state) => {
        const prefabState = state.prefabStates[instanceId];
        if (!prefabState || !prefabState.clipboardData) return;

        const actualParentId = parentId || prefabState.rootId;
        if (!actualParentId) return;

        const newId = generateObjectId();
        const newObject: PrefabGameObject = {
          ...JSON.parse(JSON.stringify(prefabState.clipboardData)),
          id: newId,
          parentId: actualParentId,
          name: `${prefabState.clipboardData.name} (Copy)`,
          children: [],
        };

        prefabState.gameObjects[newId] = newObject;

        const parent = prefabState.gameObjects[actualParentId];
        if (parent) {
          parent.children.push(newId);
        }

        prefabState.undoStack.push({
          type: 'add',
          objectId: newId,
          previousState: null,
          newState: newObject,
        });
        prefabState.redoStack = [];

        prefabState.selectedIds = [newId];

        if (state.previews[instanceId]) {
          state.previews[instanceId].isDirty = true;
        }
      });
    },

    updatePrefabComponentData: (instanceId: string, objectId: string, componentType: string, propertyName: string, value: unknown) => {
      set((state) => {
        const prefabState = state.prefabStates[instanceId];
        if (!prefabState || !prefabState.gameObjects[objectId]) return;

        const obj = prefabState.gameObjects[objectId];
        const comp = obj.components.find((c) => (c as { type?: string }).type === componentType);
        if (!comp) return;

        const data = (comp as { data?: Record<string, unknown> }).data;
        if (!data) {
          (comp as { data: Record<string, unknown> }).data = { [propertyName]: value };
        } else {
          data[propertyName] = value;
        }

        prefabState.undoStack.push({
          type: 'update',
          objectId,
          previousState: null,
          newState: null,
        });
        prefabState.redoStack = [];

        if (state.previews[instanceId]) {
          state.previews[instanceId].isDirty = true;
        }
      });
    },

    getPrefabJSON: (instanceId: string) => {
      const prefabState = get().prefabStates[instanceId];
      if (!prefabState) return '{}';

      return JSON.stringify(
        {
          gameObjects: prefabState.gameObjects,
          rootId: prefabState.rootId,
        },
        null,
        2
      );
    },

    // ========================================================================
    // Audio Actions
    // ========================================================================

    initAudioState: (instanceId: string) => {
      set((state) => {
        state.audioStates[instanceId] = {
          isPlaying: false,
          isPaused: false,
          currentTime: 0,
          duration: 0,
          volume: 1,
          isMuted: false,
          isLooping: false,
          playbackRate: 1,
        };
      });
    },

    setAudioPlaying: (instanceId: string, isPlaying: boolean) => {
      set((state) => {
        if (state.audioStates[instanceId]) {
          state.audioStates[instanceId].isPlaying = isPlaying;
          state.audioStates[instanceId].isPaused = false;
        }
      });
    },

    setAudioPaused: (instanceId: string, isPaused: boolean) => {
      set((state) => {
        if (state.audioStates[instanceId]) {
          state.audioStates[instanceId].isPaused = isPaused;
          if (isPaused) {
            state.audioStates[instanceId].isPlaying = false;
          }
        }
      });
    },

    setAudioCurrentTime: (instanceId: string, currentTime: number) => {
      set((state) => {
        if (state.audioStates[instanceId]) {
          state.audioStates[instanceId].currentTime = currentTime;
        }
      });
    },

    setAudioDuration: (instanceId: string, duration: number) => {
      set((state) => {
        if (state.audioStates[instanceId]) {
          state.audioStates[instanceId].duration = duration;
        }
      });
    },

    setAudioVolume: (instanceId: string, volume: number) => {
      set((state) => {
        if (state.audioStates[instanceId]) {
          state.audioStates[instanceId].volume = Math.max(0, Math.min(1, volume));
        }
      });
    },

    setAudioMuted: (instanceId: string, isMuted: boolean) => {
      set((state) => {
        if (state.audioStates[instanceId]) {
          state.audioStates[instanceId].isMuted = isMuted;
        }
      });
    },

    setAudioLooping: (instanceId: string, isLooping: boolean) => {
      set((state) => {
        if (state.audioStates[instanceId]) {
          state.audioStates[instanceId].isLooping = isLooping;
        }
      });
    },

    setAudioPlaybackRate: (instanceId: string, playbackRate: number) => {
      set((state) => {
        if (state.audioStates[instanceId]) {
          state.audioStates[instanceId].playbackRate = playbackRate;
        }
      });
    },

    stopAudio: (instanceId: string) => {
      set((state) => {
        if (state.audioStates[instanceId]) {
          state.audioStates[instanceId].isPlaying = false;
          state.audioStates[instanceId].isPaused = false;
          state.audioStates[instanceId].currentTime = 0;
        }
      });
    },

    // ========================================================================
    // Getters
    // ========================================================================

    getVisiblePreviews: () => {
      return Object.values(get().previews)
        .filter((p) => p.state === 'normal')
        .sort((a, b) => a.zIndex - b.zIndex);
    },

    getMinimizedPreviews: () => {
      return Object.values(get().previews)
        .filter((p) => p.state === 'minimized')
        .sort((a, b) => a.createdAt - b.createdAt);
    },
  }))
);
